import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProdutoIcmsRow {
  id: number;
  ufIcmsSt?: string | null;
  situacaoTributariaId?: number | null;
  csosnId?: number | null;
  tributacaoIcms?: string | null;
  modDeterminacaoBC?: number | null;
  percentualReducaoBC?: number | null;
  aliquotaIcms?: number | null;
  percentualBCOperacaoPropria?: number | null;
  aliquotaIcmsAproveitado?: number | null;
  destacarIcms: boolean;
  modDeterminacaoBCSt?: number | null;
  percentualReducaoBCSt?: number | null;
  aliquotaIcmsSt?: number | null;
  margemValorAdicionadoSt?: number | null;
  precoBaseSubstituicao?: number | null;
  motivoDesoneracao?: string | null;
  cBnef?: string | null;
  aliquotaCbs: number;
  aliquotaIbsUf: number;
  aliquotaIbsMunicipio: number;
}

export type SalvarProdutoIcmsRequest = Omit<ProdutoIcmsRow, "id">;

export const useProdutoIcms = (produtoId: string | undefined) => {
  const qc = useQueryClient();
  const numId = produtoId ? Number(produtoId) : undefined;
  const key = ["produto-icms", numId];
  const base = `/api/produtos/${numId}/icms`;

  const query = useQuery({
    queryKey: key,
    queryFn: async () => (await apiClient.get<ProdutoIcmsRow[]>(base)).data,
    enabled: !!numId,
  });

  const create = useMutation({
    mutationFn: async (body: SalvarProdutoIcmsRequest) => (await apiClient.post<ProdutoIcmsRow>(base, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("ICMS por UF adicionado!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
  const update = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SalvarProdutoIcmsRequest }) =>
      (await apiClient.put<ProdutoIcmsRow>(`${base}/${id}`, data)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("ICMS por UF atualizado!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
  const remove = useMutation({
    mutationFn: async (id: number) => { await apiClient.delete(`${base}/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("ICMS por UF removido!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });

  return {
    icms: query.data ?? [],
    createIcms: create.mutate,
    updateIcms: update.mutate,
    deleteIcms: remove.mutate,
  };
};
